/**
 * Dynamic tracker loaders, gated by cookie consent (GDPR/ePrivacy).
 *
 * - GTM (analytics) and Amplitude (analytics) load only when user grants
 *   `analytics` consent.
 * - Sentry init runs only when user grants `errorTracking` consent.
 * - Session Replay (Amplitude or Sentry) is intentionally NOT loaded.
 *
 * The functions are idempotent — repeated calls are no-ops once the script
 * has been injected.
 */

const GTM_ID = 'GTM-N3GTH58B';
const AMPLITUDE_API_KEY = '8e1202d1debf224a38959d087b9912b8';

let gtmLoaded = false;
let amplitudeLoaded = false;
let sentryInitialized = false;

export function loadGTM() {
  if (gtmLoaded) return;
  if (typeof document === 'undefined') return;
  gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function loadAmplitude() {
  if (amplitudeLoaded) return;
  if (typeof document === 'undefined') return;
  amplitudeLoaded = true;

  const script = document.createElement('script');
  script.src = `https://cdn.amplitude.com/script/${AMPLITUDE_API_KEY}.js`;
  script.async = true;
  script.onload = () => {
    if (!window.amplitude) return;
    // Session Replay intentionally NOT enabled — see Cookie Policy.
    window.amplitude.init(AMPLITUDE_API_KEY, {
      fetchRemoteConfig: true,
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: {
          thrashedCursor: true,
          errorClicks: true,
          deadClicks: true,
          rageClicks: true,
        },
      },
    });
  };
  document.head.appendChild(script);
}

// Sentry is initialized lazily by initSentry() so the static import lives
// in this module only — main.jsx no longer pulls it eagerly. Other files
// (e.g. FeedbackWidget) use Sentry too; that's fine — Sentry's init is
// idempotent and our consent gate guarantees it runs only after consent.
import * as Sentry from '@sentry/react';
import { useLocation, useNavigationType, createRoutesFromElements, matchRoutes } from 'react-router-dom';
import { useEffect as reactUseEffect } from 'react';

export function initSentry() {
  if (sentryInitialized) return;
  if (typeof window === 'undefined') return;
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  sentryInitialized = true;

  const isProd = import.meta.env.VITE_ENVIRONMENT === 'production';

  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: reactUseEffect,
          useLocation,
          useNavigationType,
          createRoutesFromElements,
          matchRoutes,
        }),
        Sentry.feedbackIntegration({
          autoInject: false,
          showName: false,
          showEmail: false,
          useSentryUser: { email: 'email', name: 'username' },
          colorScheme: 'system',
          formTitle: 'Send feedback',
          messageLabel: 'Message',
          messagePlaceholder: 'Question, idea or anything that bugs you?',
          submitButtonLabel: 'Send',
          cancelButtonLabel: 'Cancel',
          successMessageText: 'Thanks — we got it.',
          triggerLabel: 'Feedback',
        }),
      ],
      tracesSampleRate: isProd ? 0.2 : 1.0,
      // Session Replay disabled — see Cookie Policy / Privacy Policy.
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry] init failed', e);
  }
}
